import { Icon } from "@/assets/icons";
import { memo, ReactNode, useCallback } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import {
  FieldValues,
  useController,
  UseControllerProps,
  useFieldArray,
} from "react-hook-form";
import { toast } from "react-toastify";

interface FormDropzoneMultipleProps<TFieldValues extends FieldValues> {
  name: UseControllerProps<TFieldValues>["name"];
  nameDelete?: UseControllerProps<TFieldValues>["name"];
  control: UseControllerProps<TFieldValues>["control"];
  content?: ReactNode;
  accept?: Record<string, string[]>;
  errorFileSize?: string;
  errorInvalidType?: string;
  maxSizeInBytes?: number;
  errorMaxFiles?: string;
  containerClassName?: string;
  disabled?: boolean;
  maxFiles?: number;
  syncMode?: "append" | "replace";
}

type FileItem = {
  id?: number;
  file?: File;
  url?: string;
  name?: string;
};

const FormDropzoneMultiple = <TFieldValues extends FieldValues>({
  name,
  nameDelete,
  control,
  content,
  accept,
  errorFileSize = "ファイルサイズが大きすぎます",
  errorInvalidType = "無効なファイルタイプです",
  errorMaxFiles = "アップロード可能なファイル数を超えています",
  maxSizeInBytes,
  containerClassName,
  disabled,
  maxFiles,
  syncMode = "append",
}: FormDropzoneMultipleProps<TFieldValues>) => {
  const { fieldState } = useController({ name, control });
  const { fields, append, remove, replace } = useFieldArray({
    name: name as unknown as any,
    control,
    keyName: "_id",
  });

  const { append: appendDeleteIds } = useFieldArray({
    name: nameDelete as unknown as any,
    control,
    keyName: "_id",
  });

  const handleRejectFiles = (rejectedFiles: FileRejection[]) => {
    if (!rejectedFiles.length) return;

    const first = rejectedFiles[0];
    const firstError = first.errors[0];

    switch (firstError.code) {
      case "file-too-large":
        toast.error(errorFileSize);
        break;
      case "file-invalid-type":
        toast.error(errorInvalidType);
        break;
      case "too-many-files":
        toast.error(errorMaxFiles);
        break;
      default:
        toast.error(
          firstError.message || "ファイルのアップロードに失敗しました"
        );
    }
  };

  const handleFileAccepts = useCallback(
    (files: File[]) => {
      // valiate number of files
      if (fields.length + files.length > (maxFiles || Infinity)) {
        toast.error(`最大アップロード数は ${maxFiles} 件です`);
        return;
      }

      if (syncMode === "replace") {
        replace(files.map((file) => ({ file } as unknown as any)));
      } else {
        append(files.map((file) => ({ file } as unknown as any)));
      }
    },
    [append, fields, maxFiles]
  );

  const handleRemove = (index: number, item: FileItem) => {
    remove(index);
    if (!nameDelete) return;
    if (item.id) {
      appendDeleteIds(item.id as unknown as any);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: accept,
    maxSize: maxSizeInBytes,
    onDropAccepted: handleFileAccepts,
    onDropRejected: handleRejectFiles,
    disabled,
    maxFiles,
  });

  const displayContent = content || (
    <div className="flex flex-col gap-2 justify-center items-center">
      <div className="text-sm text-light_primary leading-5.5">
        ファイルを添付してアップロードする
      </div>
      <div className="text-xs text-light_secondary leading-5.5">
        推奨タイプ : MP4, PDF, PNG, JPG, JPGE
      </div>
      <div className="text-sm text-cyan underline leading-5.5">
        アップロード
      </div>
    </div>
  );

  const error = fieldState.error?.message;
  const fileItems: FileItem[] = (fields as unknown as any) || [];

  return (
    <div className={containerClassName}>
      <div
        {...getRootProps()}
        className="border-dashed border border-component_input_outline rounded-lg bg-grey-200 p-4"
      >
        <input {...getInputProps()} />
        {displayContent}
      </div>

      {error && <p className="text-error_main text-xs mt-1">{error}</p>}

      {/* Render Files */}
      <div className="mt-4 space-y-2">
        {fileItems.map((item, index) => (
          <FileItemDisplay
            key={item.id}
            item={item}
            onRemove={() => handleRemove(index, item)}
          />
        ))}
      </div>
    </div>
  );
};

function FileItemDisplay({
  item,
  onRemove,
}: {
  item: FileItem;
  onRemove: () => void;
}) {
  const fileName = item.name || item.file?.name || item.url;

  return (
    <div className="flex items-center gap-2">
      <div className="w-[30px] flex items-center justify-center">
        <FilePreview file={item.file} url={item.url} />
      </div>
      <span
        title={fileName}
        className="max-w-[200px] truncate text-light_primary text-sm leading-5.5"
      >
        {fileName}
      </span>
      <button
        type="button"
        className="text-error_main hover:opacity-75 transition-opacity duration-200"
        onClick={onRemove}
      >
        <Icon name="close" />
      </button>
    </div>
  );
}

const FilePreview = memo(({ file, url }: { file?: File; url?: string }) => {
  if (!file && !url) return null;

  const detectFileTypeFromFile = (file: File) => {
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const isPdf = file.type === "application/pdf";
    return { isImage, isVideo, isPdf };
  };

  const detectFileTypeFromUrl = (
    url: string
  ): "image" | "video" | "pdf" | "unknown" => {
    const lowerUrl = url.toLowerCase();

    if (/\.(jpg|jpeg|png|gif|webp|svg)$/.test(lowerUrl)) {
      return "image";
    }
    if (/\.(mp4|mov|avi|mkv|webm)$/.test(lowerUrl)) {
      return "video";
    }

    if (lowerUrl.endsWith(".pdf")) {
      return "pdf";
    }

    return "unknown";
  };

  if (file) {
    const { isImage, isVideo, isPdf } = detectFileTypeFromFile(file);

    if (isImage)
      return (
        <img
          src={URL.createObjectURL(file)}
          alt={file.name}
          className="size-[30px] object-cover rounded-lg overflow-hidden"
        />
      );

    if (isVideo)
      return (
        <video
          src={URL.createObjectURL(file)}
          controls={false}
          className="size-[30px] object-cover rounded-lg overflow-hidden"
          autoPlay={false}
        />
      );

    if (isPdf) return <Icon name="pdf" />;

    return null;
  }

  // Preview url
  const type = detectFileTypeFromUrl(url as unknown as string);

  if (type === "image")
    return (
      <img
        src={url}
        alt={url}
        className="size-[30px] object-cover rounded-lg overflow-hidden"
      />
    );

  if (type === "video")
    return (
      <video
        src={url}
        controls={false}
        className="size-[30px] object-cover rounded-lg overflow-hidden"
        autoPlay={false}
      />
    );

  if (type === "pdf") return <Icon name="pdf" />;

  return null;
});

export { FormDropzoneMultiple };
